import { FLOW, SITUATION, planRef, pubNumber } from "./flowCopy";
import { shortDate } from "./shared";
import type {
  AccountPlanOverview,
  PlanFile,
  PlanRejectedUpload,
} from "../../Types/AccountPlan";
import { STALLED_LINE } from "../../Utils/AccountPlans/queueText";

/**
 * The plan page's state, derived from the overview payload alone (upload-flow
 * Phase 1, design note QyNFoApoV4Fg2VYcyJ2fFJ). Nothing here is stored: the
 * status strip, the stepper and the Plan tab's status card all read this one
 * derivation, so they can't disagree about which version is in progress.
 *
 * Words: the version in progress is the current (newest usable) version; the
 * published version is what the sponsor has (`last_published`). A newer upload
 * that stopped (failed, rejected, waiting for an OK) is not a version yet; it is
 * reported in the strip ("New upload needs fixing") but never ticks or unticks a step.
 */

export type FlowTone = "slate" | "blue" | "violet" | "amber" | "emerald";

export interface PlanFlow {
  /** The version in progress (the current, newest usable one). */
  current: PlanFile | null;
  /** The newest published version: what the sponsor has. */
  published: {
    /** Internal upload number: compare with file `version`, never show. */
    version: number;
    /** The plan number people see (D1). */
    planVersion: number;
    at: string;
    fileId: string;
  } | null;
  /** The current version is the published one. */
  currentPublished: boolean;
  /** An older version is published while a newer one is in progress. */
  olderPublished: boolean;
  /** The newest upload, when it stopped before becoming the current version. */
  stopped: PlanFile | null;
  /** The newest upload, when it is still being read and is newer than current. */
  reading: PlanFile | null;
  /**
   * Confirm plan (Phase 2): the current version is confirmed by the AE. Only an
   * explicit `false` from the server counts as unconfirmed, so a payload from
   * before the Confirm step reads as confirmed.
   */
  currentConfirmed: boolean;
  /**
   * The upload waiting on Confirm plan: the newest upload when the checks asked
   * about it (needs_review) or stopped it with checks the AE may overrule with a
   * reason (an account warning, F-UF-E08), else the current version while it is
   * unconfirmed.
   */
  toConfirm: PlanFile | null;
  /** Owned sections verified / owned, for the current version's review. */
  counts: { verified: number; owned: number } | null;
  /**
   * The outstanding pre-parse rejection (Phase 3), for the AE only: null for
   * anyone else, so the strip reads the same for them as before it.
   */
  rejection: PlanRejectedUpload | null;
  /** Status strip: chip and one line about the version in progress. */
  chip: { label: string; tone: FlowTone };
  line: string;
}

const STOPPED = new Set(["failed", "rejected", "needs_review"]);

/**
 * A rejected upload the AE may still use, with a written reason (design §08,
 * "Use it anyway"): every failed check is one the server lets them overrule.
 */
export const canUseAnyway = (f: PlanFile): boolean => {
  if (f.status !== "rejected") return false;
  const fails = (f.validation?.checks || []).filter(c => c.status === "fail");
  return fails.length > 0 && fails.every(c => c.overridable);
};

/**
 * The outstanding pre-parse rejection (Phase 3): the newest record not dismissed
 * and not followed by any upload (mirrors the Plans list's `last_rejection`).
 * Only the AE acts on it; it never changes the status anyone else sees.
 */
export const pendingRejection = (
  files: PlanFile[],
  rejections: PlanRejectedUpload[] | undefined | null
): PlanRejectedUpload | null => {
  const newest = [...(rejections || [])]
    .filter(r => !!r.at)
    .sort((a, b) => (b.at as string).localeCompare(a.at as string))[0];
  if (!newest || newest.dismissed) return null;
  const at = new Date(newest.at as string).getTime();
  const later = files.some(
    f => !!f.created_at && new Date(f.created_at).getTime() >= at
  );
  return later ? null : newest;
};

/** D40: "Plan is queued up for parsing" while it waits its turn, else "Parsing the slides". */
const parseLine = (f: PlanFile): string =>
  f.queue?.read?.state === "queued" ? FLOW.queuedForParsing : FLOW.parsing;

export const planFlow = (data: AccountPlanOverview): PlanFlow => {
  // A discarded upload ("Not this file") never happened as far as the status goes:
  // the account reads as it did before it (Phase 2, design D3).
  const files = data.files
    .filter(f => !f.discarded)
    .sort((a, b) => b.version - a.version);
  const latest = files[0] || null;
  const current = data.files.find(f => f.id === data.current_file_id) || null;
  const review = data.current_review;

  const lp = data.last_published;
  // The newest published version. The current version's own publish record comes
  // FIRST: last_published excludes the current version, so once v2 is published it
  // names v1 (the one before), and taking it first showed "Published · v1" with
  // "Open published plan (v1)" after v2 went out (Sushant's update test, 2026-10-31).
  const published =
    review?.published && current
      ? {
          version: current.version,
          planVersion:
            review.published.plan_version ??
            current.plan_version ??
            current.version,
          at: review.published.at ?? null,
          fileId: current.id,
        }
      : lp
        ? {
            version: lp.version,
            planVersion: pubNumber(lp) as number,
            at: lp.at,
            fileId: lp.file_id,
          }
        : null;
  const currentPublished =
    !!review?.published ||
    (!!published && !!current && published.fileId === current.id);
  const olderPublished = !!published && !currentPublished;

  const newer = !!latest && (!current || latest.version > current.version);
  const stopped = newer && latest && STOPPED.has(latest.status) ? latest : null;
  const reading =
    newer && latest && latest.status === "processing" ? latest : null;

  const currentConfirmed =
    !current ||
    (data.current_confirmed !== false && current.confirmed !== false);
  const toConfirm =
    stopped &&
    !stopped.discarded &&
    !stopped.purged &&
    (stopped.status === "needs_review" || canUseAnyway(stopped))
      ? stopped
      : current && !currentConfirmed
        ? current
        : null;

  const counts = review
    ? {
        verified: review.progress.AE.verified + review.progress.CSM.verified,
        owned: review.progress.AE.owned + review.progress.CSM.owned,
      }
    : null;

  const still = published ? `v${published.planVersion} still published` : null;
  const parts = (...p: (string | null | false | undefined)[]) =>
    p.filter(Boolean).join(" · ");

  let chip: PlanFlow["chip"];
  let line: string;
  const inReview =
    !!current &&
    !currentPublished &&
    !!review &&
    review.status !== "none" &&
    !review.published;

  const rejection = data.me?.roles?.includes("AE")
    ? pendingRejection(data.files, data.rejected_uploads)
    : null;

  if (rejection) {
    // Refused before reading: nothing changed, the AE uploads a different file.
    chip = { label: FLOW.rejected, tone: "amber" };
    line = parts(
      FLOW.lastUploadRejected,
      inReview && current ? `${planRef(current)} in review` : null,
      still ?? (latest ? "not published yet" : "no plan yet")
    );
  } else if (!latest) {
    chip = { label: "No plan", tone: "slate" };
    // D16: the line doesn't repeat the chip; anyone but the AE waits on the AE.
    line = data.me?.roles?.includes("AE")
      ? "No plan uploaded yet"
      : FLOW.waitingAeUpload;
  } else if (stopped && !published && !data.me?.roles?.includes("AE")) {
    // Q9 (Sushant, 2026-10-31): a stopped first upload: anyone but the AE sees only
    // that the AE is on it, never why it stopped.
    chip = { label: "In progress", tone: "blue" };
    line = FLOW.aeReviewingUpload;
  } else if (stopped) {
    chip = {
      label:
        stopped.status === "needs_review"
          ? SITUATION.needs_ok
          : SITUATION.needs_fixing,
      tone: "amber",
    };
    line = parts(
      // Stopped uploads lose their version number (design D1): name it, don't number it.
      "New upload needs review",
      inReview && current ? `${planRef(current)} in review` : null,
      still ?? "no published plan yet"
    );
  } else if (reading) {
    chip = { label: "In progress", tone: "blue" };
    line = parts(
      reading.progress?.stalled ? STALLED_LINE : parseLine(reading),
      inReview && current ? `${planRef(current)} in review` : null,
      still
    );
  } else if (current && !currentConfirmed) {
    // Read, waiting on the AE (the strip isn't viewer-aware: the chip names the step).
    chip = { label: SITUATION.to_confirm, tone: "amber" };
    line = parts(FLOW.newUploadWaiting, still ?? "no published plan yet");
  } else if (current && currentPublished && published) {
    chip = { label: "Published", tone: "emerald" };
    line = parts(`v${published.planVersion}`, shortDate(published.at));
  } else if (current && inReview) {
    chip = { label: "In review", tone: "violet" };
    line = parts(planRef(current), still ?? "not published yet");
  } else if (current) {
    const writing = current.sponsor_view_status === "processing";
    const failed = current.sponsor_view_status === "failed";
    // F-UF-F11: a briefing that didn't finish is the AE's to retry, as the Plans
    // list says ("Action needed · Try again"): amber, not "In progress".
    chip = failed
      ? { label: SITUATION.briefing_failed, tone: "amber" }
      : { label: "In progress", tone: "blue" };
    line = parts(
      writing
        ? current.progress?.stalled
          ? STALLED_LINE
          : current.queue?.brief?.state === "queued"
            ? FLOW.briefingQueued
            : `${FLOW.writingBriefingFor} ${planRef(current)}`
        : failed
          ? `Briefing for ${planRef(current)} didn't finish`
          : `${planRef(current)} · no briefing yet`,
      still ?? "not published yet"
    );
  } else {
    // Only uploads still being read for the first time.
    chip = { label: "In progress", tone: "blue" };
    // Never numbered before confirm (D1).
    line = latest?.progress?.stalled
      ? STALLED_LINE
      : latest
        ? parseLine(latest)
        : FLOW.parsing;
  }

  return {
    current,
    published,
    currentPublished,
    olderPublished,
    stopped,
    reading,
    currentConfirmed,
    toConfirm,
    counts,
    rejection,
    chip,
    line,
  };
};

/**
 * The version plan chat answers from (F-UF-F01): the newest version the AE
 * confirmed, never an unconfirmed or discarded upload. The backend's
 * current_file_id is the newest READ upload, confirmed or not, and the chat
 * service refuses an unconfirmed one ("not_confirmed"). Only an explicit
 * `confirmed: false` counts as unconfirmed, so payloads from before the Confirm
 * step still ask. Since 2026-10-31 chat also waits for the version's briefing:
 * it opens once the briefing is ready (published or not), never while it is
 * still being written (the chat service refuses with "briefing_not_ready").
 * Since 2026-11-01 it also waits for the publish: chat answers only from a
 * published version, for the team and the sponsor ("not_published").
 * Returns the payload with current_file_id / current_review pointed at that
 * version (current_file_id null when there is none).
 */
export const isChatReady = (f: PlanFile): boolean =>
  (f.status === "ready" || f.status === "partial") &&
  f.confirmed !== false &&
  !f.discarded &&
  f.sponsor_view_status === "ready" &&
  // 2026-11-01 (product review): chat answers only from a published version.
  !!f.published;

/** Confirmed and read, with its briefing still to come: chat waits on the briefing. */
export const awaitsBriefing = (f: PlanFile): boolean =>
  (f.status === "ready" || f.status === "partial") &&
  f.confirmed !== false &&
  !f.discarded &&
  f.sponsor_view_status !== "ready";

export const withChatFile = <T extends AccountPlanOverview>(data: T): T => {
  const cur = data.files.find(f => f.id === data.current_file_id);
  if (cur && isChatReady(cur)) return data;
  // A sponsor's / reader's payload already points current_file_id at the
  // version they read; with no such file in `files`, leave it as is.
  if (!cur && data.current_file_id) return data;
  const target = data.files.find(isChatReady) ?? null;
  if (!target) return { ...data, current_file_id: null, current_review: null };
  const review =
    target.id === data.published_file_id
      ? data.published_review
      : target.id === data.sponsor_file_id
        ? data.sponsor_review
        : null;
  return {
    ...data,
    current_file_id: target.id,
    current_review: review ?? null,
  };
};
